import React, { forwardRef, useMemo, useState } from 'react';
import type { Node } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import get from 'lodash/get';
import noop from 'lodash/noop';
import { useAppType } from '@vmos2/providers';
import { css, keyframes, useTheme } from '@emotion/react';
import { tint } from 'polished';

import { IconButton } from '../buttons';
import { PhonePrefixSelector } from './PhonePrefixSelector';
import { getSpacingStyles } from '../withSpacingStyles';

type Props = {
  /**
   * This prop defines a label the element.
   */
  label?: string,
  /**
   * Calback triggered on phone prefix change
   */
  phonePrefixHandleChange?: Function,
  /**
   * Defines the default phone prefix
   */
  phonePrefixDefault?: string,
  /**
   * Defines the data set for phone prefixes. If present, the phone prefix will be added.
   */
  phonePrefixData?: mixed[],
  /**
   * This prop defines a prefix text or icon for element.
   */
  prefix?: string | Node,

  /**
   * This prop defines a suffix text or icon for element.
   */
  suffix?: string | Node,
  /**
   * This prop defines a message beneath the input.
   */
  description?: string,
  /**
   * Type of the `input` element. It should be [a valid HTML5 input type](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Form_%3Cinput%3E_types) from the list.
   */
  type: 'text' | 'password' | 'email' | 'date' | 'number' | 'tel' | 'time',
  /**
   * If not empty, the input will indicate an error.
   */
  error?: string,
  /**
   * Name attribute of the `input` element.
   */
  name: string,
  /**
   * This prop helps users to fill forms faster, especially on mobile devices.
   * You can learn more about it [following the specification](https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill).
   */
  autoComplete?: HTMLInputElement['autocomplete'],
  /**
   * If `true`, the `input` element will be focused during the first mount.
   */
  autoFocus?: boolean,
  /**
   * If `true`, the `input` element will be disabled.
   */
  disabled?: boolean,
  /**
   * If `true`, the input will take up the full width of its container.
   */
  fullWidth?: boolean,
  /**
   * Callback fired when the value is changed.
   */
  handleChange?: Function,
  /**
   * If `true`, the input will cave CAPITAL letters
   */
  upperCase?: boolean,
  /**
   * The short hint displayed in the input before the user enters a value.
   */
  placeholder?: string,
  /**
   * If `true`, the `input` element will be required.
   */
  required?: boolean,
  /**
   * If `true`, the `loading` element will be required.
   */
  loading?: boolean,
  /**
   * This prop add a button inside the field
   */
  insideButton?: Node,
  /**
   * This prop specifies a regular expression that the <input> element's value is checked against on form submission.
   */
  pattern?: string,
  /**
   * This prop describes the pattern to help user.
   */
  title?: string,
  /**
   * This prop specifies the maximum number of characters allowed in the <input> element.
   */
  maxLength?: string,
  /**
   * This prop defines assistive text to give user more context.
   */
  assistiveText?: string,
  /**
   * This prop defines size of the element
   */
  size: 'large' | 'small' | 'xsmall',
  /**
   * The value of the `input` element, required for a controlled component.
   */
  value?: mixed,

  /**
   * This prop defines a styling for the input group
   */
  groupStyle?: string,

  /**
   * React Final Form meta prop
   */
  meta: {
    touched: boolean,
    error: string,
    dirty: boolean,
    submitFailed: boolean,
  },
  optional: boolean,
  showErrorOnBlur?: boolean,
  showError?: boolean,
  showLabelError?: boolean,
  /**
   * React Final Form input prop
   */
  input: mixed,
  /**
   * Prevents from entering non-numberical characters that number fields allow by default
  */
  preventNonNumericEntry?: boolean,
  /**
   * Prop to conditionally apply quantity field specific style
  */
  isQuantityField?: boolean,
  /**
   * Show a button within the input that toggles the 'password' type on and off.
   */
  showContentButton?: boolean
};

const inputStyles = {
  large: {
    label: {
      fontSize: theme => theme.fontSize.md,
      fontWeight: theme => theme.fontWeight.bold,
    },
    input: {
      fontSize: theme => theme.fontSize.md,
      prefixPadding: '10px 16px 10px 8px',
      suffixPadding: '18px 24px',
      padding: '10px 16px',
      'margin-bottom': '8px',
      'margin-top': '8px',
    },
    assistiveText: {
      fontSize: theme => theme.fontSize.sm,
    },
    error: {
      fontSize: theme => theme.fontSize.sm,
    },
  },

  small: {
    input: {
      fontSize: theme => theme.fontSize.md,
      prefixPadding: '10px 16px 10px 8px',
      suffixPadding: '12px 16px',
      padding: '10px 16px',
      'margin-bottom': '8px',
      'margin-top': '8px',
    },
    label: {
      fontSize: theme => theme.fontSize.md,
      fontWeight: theme => theme.fontWeight.regular,
    },
    assistiveText: {
      fontSize: theme => theme.fontSize.sm,
    },
    error: {
      fontSize: theme => theme.fontSize.sm,
    },
  },

  xsmall: {
    input: {
      fontSize: theme => theme.fontSize.md,
      prefixPadding: '10px 16px 10px 8px',
      suffixPadding: '3px',
      padding: '3px 16px',
      'margin-bottom': '8px',
      'margin-top': '8px',
    },
    label: {
      fontSize: theme => theme.fontSize.md,
      fontWeight: theme => theme.fontWeight.regular,
    },
    assistiveText: {
      fontSize: theme => theme.fontSize.sm,
    },
    error: {
      fontSize: theme => theme.fontSize.sm,
    },
  },
};

export const Input = forwardRef(
  (
    {
      assistiveText,
      autoComplete,
      description,
      disabled,
      error,
      handleChange,
      input,
      insideButton,
      isQuantityField,
      label,
      loading,
      meta,
      name,
      optional,
      phonePrefixData,
      phonePrefixDefault,
      phonePrefixHandleChange,
      prefix,
      preventNonNumericEntry,
      showContentButton,
      showError,
      showLabelError,
      showErrorOnBlur,
      size,
      suffix,
      type,
      upperCase,
      groupStyle,
      maxLength,
      ...rest
    }: Props,
    ref
  ) => {
    const spacingStyles = getSpacingStyles(rest);
    const Tag = spacingStyles ? 'div' : React.Fragment;
    const initialInputType = type || input?.type;
    const { isKiosk } = useAppType();
    const [isInputContentHidden, setIsInputContentHidden] = useState(initialInputType === 'password');
    const theme = useTheme();

    const getInputType = () => {
      if (showContentButton) {
        if (isInputContentHidden) {
          return 'password';
        }

        if (initialInputType === 'password') {
          return 'text';
        }
      }

      return initialInputType;
    };

    const removeAutoCompleteProps = isKiosk && {
      autoComplete: 'off',
      autoCorrect: 'off',
      autoCapitalize: 'off',
      spellCheck: false,
    };

    const { t } = useTranslation('validations');
    const inputName = get(input, 'name', name);
    const onChange = e => {
      input && input.onChange(e.nativeEvent);
      handleChange(e.target.value);
    };

    const hasError = useMemo(() => {
      if (showError) return true;
      const isDirty = meta && (showErrorOnBlur ? meta.touched : (meta.dirty || meta.submitFailed) && meta.touched);
      return error || (isDirty && meta.error);
    }, [error, showErrorOnBlur, meta, showError]);

    const handleKeyPress = (e) => {
      type === 'number' &&
        preventNonNumericEntry &&
        ['-', '+', 'e', '.', ','].includes(e.key) &&
        e.preventDefault();
    };

    return (
      <Tag {...(spacingStyles && { css: css`${spacingStyles}` })}>
        {label && (
          <FormLabel data-test="input-label" htmlFor={inputName} size={size} hasError={showLabelError}>
            {label}
          </FormLabel>
        )}
        {loading && (
          <LoadingRing>
            <div />
            <div />
            <div />
            <div />
          </LoadingRing>
        )}
        {insideButton}

        <InputGroup
          data-test="input-wrapper"
          size={size}
          phonePrefix={phonePrefixData}
          assistiveText={assistiveText}
          optional={optional}
          label={label}
          error={hasError}
          disabled={disabled}
          css={groupStyle}
        >
          {phonePrefixData && (
            <PhonePrefixSelector
              changeHandler={phonePrefixHandleChange}
              defaultPhonePrefix={phonePrefixDefault}
              phonePrefixData={phonePrefixData}
            />
          )}

          {prefix && <InputPrefix size={size}>{prefix}</InputPrefix>}

          <FormInput
            autoComplete={autoComplete}
            data-test={inputName}
            disabled={disabled}
            error={hasError}
            hasPrefix={!!phonePrefixData}
            id={inputName}
            isQuantityField={isQuantityField}
            onKeyPress={handleKeyPress}
            prefix={prefix}
            ref={ref}
            size={size}
            suffix={suffix}
            upperCase={upperCase}
            {...input}
            {...rest}
            {...removeAutoCompleteProps}
            onChange={onChange}
            type={getInputType()}
            maxLength={maxLength}
          />

          {suffix && <InputSuffix size={size}>{suffix}</InputSuffix>}

          {showContentButton && (
            <IconButton
              aria-label={`Show ${label || rest.placeholder || 'password' }`}
              aria-pressed={!isInputContentHidden}
              data-test="input-show-content-button"
              fill={theme.color.grey}
              icon={isInputContentHidden ? 'EyeSlash' : 'Eye'}
              mode="ghost"
              onClick={() => setIsInputContentHidden(previousInputType => !previousInputType)}
              type="button"
            />
          )}
        </InputGroup>

        {(assistiveText || optional) && (
          <AssistiveText data-test="input-assistive-text" size={size}>
            {[assistiveText, optional && t('optional')].filter(Boolean).join(' ')}
          </AssistiveText>
        )}

        {!hasError && description && <Description size={size}>{description}</Description>}

        {hasError && (
          <Error size={size} data-test={`${inputName}-validation-msg`}>
            {hasError}
          </Error>
        )}
      </Tag>
    );
  }
);

Input.defaultProps = {
  optional: false,
  size: 'large',
  showErrorOnBlur: true,
  handleChange: noop,
};

const FormLabel = styled('label')`
  color: ${({ theme, hasError }) => hasError ? theme.color.danger : theme.color.black};
  font-size: ${({ size, theme }) => inputStyles[size].label.fontSize(theme)};
  font-weight: ${({ size, theme }) => inputStyles[size].label.fontWeight(theme)};
`;

const loadingRing = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const LoadingRing = styled('div')`
  position: absolute;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  margin: 0 auto;
  right: 12px;
  top: 10px;

  div {
    box-sizing: border-box;
    display: block;
    position: absolute;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    top: 0;
    left: 0;
    animation: ${loadingRing} 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;

    &:after {
      content: '';
      height: 4px;
      width: 4px;
      background: ${({ theme }) => theme.color.grey};
      border-radius: 20px;
      top: 2px;
      left: 2px;
      position: absolute;
    }

    &:before {
      content: '';
      height: 4px;
      width: 4px;
      background: ${({ theme }) => theme.color.grey};
      border-radius: 20px;
      top: 2px;
      left: 2px;
      position: absolute;
    }
  }
  div:nth-of-type(1) {
    animation-delay: -0.45s;
  }
  div:nth-of-type(2) {
    animation-delay: -0.3s;
  }
  div:nth-of-type(3) {
    animation-delay: -0.15s;
  }
`;

const InputGroup = styled('div')`
  display: flex;
  position: ${({ phonePrefix }) => phonePrefix && `relative`};
  ${({ fullWidth }) => fullWidth && `width: 100%;`}
  ${({ size, error, assistiveText, optional }) =>
    (error || assistiveText || optional) && `margin-bottom: ${inputStyles[size].input['margin-bottom']};`}
  ${({ size, label }) => label && `margin-top: ${inputStyles[size].input['margin-top']};`}
  font-size: ${({ size, theme }) => inputStyles[size].input.fontSize(theme)};
  border: ${({ theme }) => theme.stroke.default};
  ${({ theme, error }) => error && `border-color: ${theme.color.danger}`};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  ${({ theme, disabled }) => disabled && `border-color: ${tint(0.6, theme.color.lightGrey)}`};

  @media (${({ theme }) => theme.breakpoint.kiosk}) {
    font-size: ${({ size, theme }) => (size === 'small' ? theme.fontSize.lg : theme.fontSize.xl)};
  }
`;

const addons = ({ theme, size }) => css`
  display: inline-flex;
  color: ${theme.color.grey};
  padding: ${inputStyles[size].input.padding};
  border-radius: inherit;
  align-items: center;
`;

const InputPrefix = styled('span')`
  ${addons};
  padding-right: 8px;
  @media (${({ theme }) => theme.breakpoint.kiosk}) {
    padding: ${({ size }) => inputStyles[size].input.suffixPadding};
  }
`;

const InputSuffix = styled('span')`
  ${addons};
  padding-left: 8px;
  padding-right: ${({ size }) => size === 'xsmall' && '3px'};
  @media (${({ theme }) => theme.breakpoint.kiosk}) {
    padding: ${({ size }) => inputStyles[size].input.suffixPadding};
  }
`;

const FormInput = styled('input')`
  display: block;
  width: 100%;
  background-color: ${({ theme, filled }) => filled ? theme.color.brand : theme.color.white};
  color: ${({ theme, filled }) => filled ? theme.color.white : theme.color.black};
  padding: ${({ size, hasPrefix, isQuantityField }) =>
    isQuantityField ? '10px'
      : inputStyles[size].input[hasPrefix ? 'prefixPadding' : 'padding']
  };
  transition: ${({ theme }) => theme.motion.transition};
  border: none;
  border-radius: inherit;
  line-height: 1;
  text-transform: ${({ upperCase }) => upperCase && 'uppercase'};
  font-family: inherit;
  text-align: ${({ isQuantityField }) => isQuantityField && 'center'};
  ${({ prefix }) =>
    prefix &&
    `
    padding-left: 8px;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  `};

  ${({ suffix }) =>
    suffix &&
    `
    padding-right: 8px;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  `};

  &:focus {
    outline: 0;
    border-color: ${({ theme }) => theme.color.info};
    box-shadow: ${({ theme }) => theme.boxShadow.focus};
  }

  &:disabled {
    border-color: ${({ theme }) => theme.color.grey};
    background: ${({ theme }) => theme.color.lighterGrey};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${({ theme }) => theme.color.lightGrey};
    letter-spacing: normal;
  }

  &[type='password'] {
    letter-spacing: 2px;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type=number] {
    -moz-appearance: textfield;
  }

  @media (${({ theme }) => theme.breakpoint.kiosk}) {
    padding: ${({ size }) => inputStyles[size].input.suffixPadding};
  }
`;

const AssistiveText = styled('span')`
  display: block;
  color: ${({ theme }) => theme.color.grey};
  font-size: ${({ size, theme }) => inputStyles[size].assistiveText.fontSize(theme)};
`;

const Error = styled('span')`
  color: ${({ theme }) => theme.color.danger};
  font-size: ${({ size, theme }) => inputStyles[size].error.fontSize(theme)};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

const Description = styled('span')`
  display: block;
  font-size: ${({ size, theme }) => inputStyles[size].error.fontSize(theme)};
  color: ${({ theme }) => theme.color.grey};
  margin-top: 16px;
`;
